/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import {
  QuestionChangeOptionalProps,
  QuestionReducerState,
} from '@g17eco/types/questionInterfaces';
import { UtrValueType } from '@g17eco/core';
import { hasValueDataChanged } from '../../../utils/valueData';
import { getInputValue, getNumberScale, getUnitCode, getValueData, isNaOrNr } from '../../../utils/universalTrackerValue';

export const useHasValueChanged = (
  state: Pick<QuestionReducerState, 'value' | 'valueData' | 'table' | 'displayCheckbox'>,
  props: QuestionChangeOptionalProps & { isEmptyData?: boolean; shouldEnforceTable?: boolean }
) => {
  const { value, valueData, displayCheckbox, table } = state;
  const { isEmptyData, utr, utrv, shouldEnforceTable } = props;

  return React.useMemo(() => {
    if (!utr || !utrv) {
      return false;
    }

    if (String(getInputValue(utrv)) !== String(value)) {
      return true;
    }

    if (isNaOrNr(utrv) && isEmptyData) {
      return false;
    }

    // The effective value type may differ from the UTR's configured type when the initiative
    // has opted in to a different aggregation UI (e.g., Table via shouldEnforceWeightedAggregationTableUI).
    const effectiveValueType = shouldEnforceTable ? UtrValueType.Table : utr.getValueType();

    return hasValueDataChanged({
      currentValueData: getValueData(utrv, effectiveValueType),
      nextValueData: valueData,
      displayCheckbox,
      table,
      valueType: effectiveValueType,
    });
  }, [
    utrv,
    value,
    valueData,
    table,
    displayCheckbox,
    utr,
    isEmptyData,
    shouldEnforceTable,
  ]);
};

export const useHasCommentsEvidenceChanged = (state: QuestionReducerState): boolean => {
  const { files, existingFiles, comments } = state;

  const filesHasChanged = React.useMemo(
    () => files.length !== 0 || existingFiles.findIndex(f => f.isDeleted || f.isUpdated) >= 0,
    [files, existingFiles]
  )

  return React.useMemo(() => {
    return filesHasChanged || String(comments).trim() !== ''
  },
    [
      comments,
      filesHasChanged
    ]);
};

export const useHasUnitNumberScaleChanged = (state: QuestionReducerState, props: QuestionChangeOptionalProps) => {
  const { unit, numberScale } = state;
  const { utrv, utr, surveyData } = props;
  const unitConfig = surveyData && 'unitConfig' in surveyData ? surveyData.unitConfig : undefined;
  const currentUnit = utrv && utr ? getUnitCode(utrv, utr, unitConfig) : undefined;
  const currentNumberScale = utrv && utr ? getNumberScale(utrv, utr, unitConfig) : undefined;

  return unit !== currentUnit || numberScale !== currentNumberScale;
};
